import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  getDashboardSummary,
  getTrips,
  getOilChangeDue,
  getWashDueDashboard,
  getFuelLowDashboard,
} from '../services/api';

export default function Dashboard() {
  const { vehicles, drivers, alerts, loading } = useApp();
  const [summary, setSummary] = useState({ totalTrips: 0, pendingTrips: 0, flaggedTrips: 0 });
  const [recentTrips, setRecentTrips] = useState([]);
  const [dueData, setDueData] = useState({ count: 0, vehicles: [] });
  const [washDueData, setWashDueData] = useState({ count: 0, vehicles: [] });
  const [fuelLowData, setFuelLowData] = useState({ count: 0, vehicles: [] });

  useEffect(() => {
    getDashboardSummary().then(setSummary).catch(() => {});
    getTrips().then((rows) => setRecentTrips(Array.isArray(rows) ? rows.slice(0, 10) : [])).catch(() => {});
    getOilChangeDue().then(setDueData).catch(() => setDueData({ count: 0, vehicles: [] }));
    getWashDueDashboard().then(setWashDueData).catch(() => setWashDueData({ count: 0, vehicles: [] }));
    getFuelLowDashboard().then(setFuelLowData).catch(() => setFuelLowData({ count: 0, vehicles: [] }));
  }, []);

  if (loading) return <p className="text-muted">Loading fleet data…</p>;

  const oilDueVehicles = Array.isArray(dueData?.vehicles) ? dueData.vehicles.slice(0, 5) : [];
  const washDueVehicles = Array.isArray(washDueData?.vehicles) ? washDueData.vehicles.slice(0, 5) : [];
  const fuelLowVehicles = Array.isArray(fuelLowData?.vehicles) ? fuelLowData.vehicles.slice(0, 5) : [];

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Fleet overview and maintenance reminders</p>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-label">Vehicles</div><div className="stat-value">{vehicles.length}</div></div>
        <div className="stat-card"><div className="stat-label">Drivers</div><div className="stat-value">{drivers.length}</div></div>
        <div className="stat-card"><div className="stat-label">Open alerts</div><div className="stat-value">{alerts.length}</div></div>
        <div className="stat-card"><div className="stat-label">Total trips</div><div className="stat-value">{summary.totalTrips || 0}</div></div>
        <div className="stat-card"><div className="stat-label">Active trips</div><div className="stat-value">{summary.pendingTrips || 0}</div></div>
        <div className="stat-card"><div className="stat-label">Flagged trips</div><div className="stat-value">{summary.flaggedTrips || 0}</div></div>
        <div className="stat-card">
          <div className="stat-label">Wash due</div>
          <div className="stat-value">{washDueData.count || 0}</div>
          <Link to="/wash" className="link-accent">View wash list →</Link>
        </div>
        <div className="stat-card">
          <div className="stat-label">Low fuel</div>
          <div className="stat-value">{fuelLowData.count || 0}</div>
          <Link to="/fuel" className="link-accent">View fuel status →</Link>
        </div>
        <div className="stat-card">
          <div className="stat-label">Oil change due</div>
          <div className="stat-value">{dueData.count || 0}</div>
          <Link to="/oil-change" className="link-accent">View oil change →</Link>
        </div>
      </div>

      {(washDueVehicles.length > 0 || fuelLowVehicles.length > 0 || oilDueVehicles.length > 0) && (
        <div className="form-grid cols3 mb20">
          {washDueVehicles.length > 0 && (
            <div className="card">
              <div className="card-title">Wash due</div>
              <div className="table-wrap">
              <table>
                <thead><tr><th>Vehicle</th><th>Days since wash</th></tr></thead>
                <tbody>
                  {washDueVehicles.map((v) => (
                    <tr key={v.vehicleId || v._id}>
                      <td>{v.name || '—'}</td>
                      <td className="mono">{v.wash?.daysSinceWash ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
          {fuelLowVehicles.length > 0 && (
            <div className="card">
              <div className="card-title">Low fuel</div>
              <div className="table-wrap">
              <table>
                <thead><tr><th>Vehicle</th><th>Km left</th></tr></thead>
                <tbody>
                  {fuelLowVehicles.map((v) => (
                    <tr key={v.vehicleId || v._id}>
                      <td>{v.name || '—'}</td>
                      <td className="mono">{v.kmRemaining ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
          {oilDueVehicles.length > 0 && (
            <div className="card">
              <div className="card-title">Oil change due</div>
              <div className="table-wrap">
              <table>
                <thead><tr><th>Vehicle</th><th>Km to service</th></tr></thead>
                <tbody>
                  {oilDueVehicles.map((v) => (
                    <tr key={v.vehicleId || v._id}>
                      <td>{v.name || '—'}</td>
                      <td className="mono">{v.oilChange?.kmUntilDue ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-title">Recent trips</div>
        <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th><th>Vehicle</th><th>Driver</th><th>Status</th><th>Actual km</th>
            </tr>
          </thead>
          <tbody>
            {recentTrips.length === 0 ? (
              <tr><td colSpan={5} className="text-muted">No trips yet</td></tr>
            ) : recentTrips.map((t) => (
              <tr key={t._id}>
                <td>{t.date ? new Date(t.date).toLocaleDateString() : '—'}</td>
                <td>{t.vehicle?.name || '—'}</td>
                <td>{t.driver?.name || '—'}</td>
                <td>{t.status}</td>
                <td>{t.actualKM ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
